import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RecurringPaymentFrequency, RecurringPaymentRuleStatus, } from "@/domain/entities/RecurringPaymentRule";
/**
 * Composable for shared subscription logic and formatting
 */
export function useSubscriptionUtils() {
    const { t } = useI18n();
    /**
     * Computed property for frequency options used in dropdowns
     */
    const frequencyOptions = computed(() => [
        {
            value: RecurringPaymentFrequency.Monthly,
            label: t("dashboard.subscriptions.frequency.monthly"),
        },
        {
            value: RecurringPaymentFrequency.Quarterly,
            label: t("dashboard.subscriptions.frequency.quarterly"),
        },
        {
            value: RecurringPaymentFrequency.SemiAnnual,
            label: t("dashboard.subscriptions.frequency.semiannual"),
        },
        {
            value: RecurringPaymentFrequency.Annual,
            label: t("dashboard.subscriptions.frequency.annual"),
        },
    ]);
    /**
     * Render frequency with localization
     */
    function renderFrequency(frequency) {
        const key = `dashboard.subscriptions.frequency.${frequency.toLowerCase()}`;
        return t(key);
    }
    /**
     * Render currency amount (amount is in cents)
     */
    function formatCurrency(amount, currency) {
        const amountValue = amount / 100;
        const { locale } = useI18n();
        return new Intl.NumberFormat(locale.value, {
            style: "currency",
            currency: currency,
        }).format(amountValue);
    }
    /**
     * Render date for display in the client's locale and timezone.
     * Dates from the API are in UTC — Intl.DateTimeFormat converts to the client's browser timezone.
     */
    function formatDate(date) {
        const { locale } = useI18n();
        if (!date) {
            return t("dashboard.subscriptions.table.noDate");
        }
        const dateObj = new Date(date);
        return new Intl.DateTimeFormat(locale.value, {
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }).format(dateObj);
    }
    /**
     * Format transaction day with localized ordinal suffix
     * @param day - Day of month (1-28)
     * @returns Localized string like "1st of each period"
     */
    function formatTransactionDay(day) {
        // Check for specific ordinal translation keys first
        const specificKey = `dashboard.subscriptions.detail.transactionDayOrdinal${day}`;
        const specificTranslation = t(specificKey);
        // If the translation exists (not equal to the key), use it
        if (specificTranslation !== specificKey) {
            return specificTranslation;
        }
        // Otherwise, use the generic ordinal format with interpolation
        return t("dashboard.subscriptions.detail.transactionDayOrdinal", { day });
    }
    /**
     * Get status badge configuration (color classes and text key)
     * @param status - Recurring payment rule status
     * @returns Object with colorClass and textKey properties
     */
    function getStatusConfig(status) {
        const configs = {
            [RecurringPaymentRuleStatus.Active]: {
                colorClass: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
                textKey: "dashboard.subscriptions.status.active",
            },
            [RecurringPaymentRuleStatus.Paused]: {
                colorClass: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
                textKey: "dashboard.subscriptions.status.paused",
            },
            [RecurringPaymentRuleStatus.Completed]: {
                colorClass: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
                textKey: "dashboard.subscriptions.status.completed",
            },
            [RecurringPaymentRuleStatus.Failed]: {
                colorClass: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
                textKey: "dashboard.subscriptions.status.failed",
            },
            [RecurringPaymentRuleStatus.Cancelled]: {
                colorClass: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
                textKey: "dashboard.subscriptions.status.cancelled",
            },
            [RecurringPaymentRuleStatus.PendingToken]: {
                colorClass: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
                textKey: "dashboard.subscriptions.status.pendingtoken",
            },
            [RecurringPaymentRuleStatus.PendingExecute]: {
                colorClass: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
                textKey: "dashboard.subscriptions.status.pendingexecute",
            },
            [RecurringPaymentRuleStatus.Pending]: {
                colorClass: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
                textKey: "dashboard.subscriptions.status.pending",
            },
            [RecurringPaymentRuleStatus.PaymentCreated]: {
                colorClass: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
                textKey: "dashboard.subscriptions.status.payment-created",
            },
        };
        return configs[status] || { colorClass: "bg-gray-100 text-gray-800", textKey: "" };
    }
    /**
     * Remix icon name to render inside the status Badge.
     * DS rule (Notion + Figma 4337:41552): a Badge cannot use the dot variant without an icon — so we use
     * the default variant with a left-icon that conveys the status visually.
     */
    function getStatusIcon(status) {
        const icons = {
            [RecurringPaymentRuleStatus.Active]: "ri-pulse-line",
            [RecurringPaymentRuleStatus.Paused]: "ri-pause-line",
            [RecurringPaymentRuleStatus.Cancelled]: "ri-close-circle-line",
            [RecurringPaymentRuleStatus.Completed]: "ri-check-line",
            [RecurringPaymentRuleStatus.Failed]: "ri-error-warning-line",
            [RecurringPaymentRuleStatus.PendingToken]: "ri-time-line",
            [RecurringPaymentRuleStatus.Pending]: "ri-time-line",
            [RecurringPaymentRuleStatus.PaymentCreated]: "ri-time-line",
            [RecurringPaymentRuleStatus.PendingExecute]: "ri-time-line",
        };
        return icons[status] ?? "ri-question-line";
    }
    /**
     * Get OPAL Badge theme for a subscription status
     */
    function getOpalBadgeTheme(status) {
        const themes = {
            [RecurringPaymentRuleStatus.Active]: "green",
            [RecurringPaymentRuleStatus.Paused]: "yellow",
            [RecurringPaymentRuleStatus.Cancelled]: "red",
            [RecurringPaymentRuleStatus.Completed]: "neutral",
            [RecurringPaymentRuleStatus.Failed]: "red",
            [RecurringPaymentRuleStatus.PendingToken]: "cyan",
            [RecurringPaymentRuleStatus.Pending]: "cyan",
            [RecurringPaymentRuleStatus.PaymentCreated]: "cyan",
            [RecurringPaymentRuleStatus.PendingExecute]: "cyan",
        };
        return themes[status] || "neutral";
    }
    /**
     * Check if we should show next transaction date based on status
     */
    function shouldShowNextTransactionDate(status) {
        return status === RecurringPaymentRuleStatus.Active;
    }
    return {
        frequencyOptions,
        renderFrequency,
        formatCurrency,
        formatDate,
        formatTransactionDay,
        getStatusConfig,
        getStatusIcon,
        getOpalBadgeTheme,
        shouldShowNextTransactionDate,
    };
}
